import { useMemo } from 'react';

import { useRecoilValue } from 'recoil';

import numOrDefault from 'lib/helpers/numOrDefault';
import { hasModule, iaRa } from 'lib/helpers/utility';
import { usePatientAging } from 'lib/hooks/queries/billing/usePatientAging';

import { modulesState } from 'components/state';
import { currentPractice } from 'components/practice/practiceState';
import { getPracticeName } from 'components/shared/DocumentSheet/lib/practiceLines';
import { getInvoiceDetails } from 'components/practice/BeyondBilling/ElectronicInvoicing/lib/internal/configs';
import { formatInvoiceProcedures } from 'components/practice/BeyondBilling/ElectronicInvoicing/lib/formatInvoiceProcedures';

import { getProceduresTotal } from '../../InvoiceFooter/InvoiceSummary/lib/helpers';

import { buildColumns } from './invoiceDocColumns';
import { buildInvoiceBlocks } from './buildInvoiceBlocks';
import { buildDocLines, buildParty } from './invoiceDocIdentityModel';
import { buildLegend, buildSections, isGfeRow } from './invoiceDocSections';
import {
  LEGAL,
  dateMedium,
  hiddenSet,
  isLandscape,
  memoText,
  money,
  visibleMoneyKeys
} from './invoiceDocModel';
import {
  buildAging,
  buildFooterLegal,
  buildSummaryFootnote,
  buildSummaryRows,
  sumBalances
} from './invoiceDocFooterModel';

const VARIANT_LABEL = {
  confirmed: 'confirmed balances',
  gfe: 'Good Faith Estimate',
  combined: 'confirmed balances & Good Faith Estimate'
};

/**
 * Resolves one invoice into the blocks, header lines and footer copy the sheet
 * renders (templates 7a-7f).
 *
 * @param {object} params
 * @param {object} params.invoice
 * @param {object} [params.externalInvoice]
 * @param {object} [params.practice] - Overrides the practice in state; the
 *   public invoice page has no practice session and passes the one the token
 *   resolved to.
 * @param {object} [params.modules] - Same, for the module flags.
 * @param {number} [params.surcharge]
 * @param {boolean} [params.withAging] - False where the practice-authed aging
 *   endpoint is not reachable (the public page).
 */
export const useInvoiceDocumentModel = ({
  invoice,
  externalInvoice,
  practice: practiceProp,
  modules: modulesProp,
  surcharge: surchargeProp,
  withAging = true,
  billedToAction
}) => {
  const practiceFromState = useRecoilValue(currentPractice);
  const practice = practiceProp?.id || practiceProp?.name ? practiceProp : practiceFromState;

  const modulesFromState = useRecoilValue(modulesState);
  // `modulesState` defaults to a truthy `{ loaded: false }`, so a caller outside
  // a practice session (the token page) has to hand its own modules in.
  const practiceModules =
    modulesProp || (practiceProp ? practice?.modules : null) || modulesFromState;
  const claims = hasModule(practiceModules, 'billing.claims');
  const surchargeEnabled = !!(
    hasModule(practiceModules, 'medipay.technology_fee') && practice?.surcharge_enabled
  );

  const timezone = practice?.timezone;
  const patientId = invoice?.patient?.id;
  const isAEOB = !!(externalInvoice?.aeob || invoice?.aeob);
  const status = invoice?.status;
  const hidden = useMemo(() => hiddenSet(invoice?.hidden_fields), [invoice?.hidden_fields]);

  const { data: agingData } = usePatientAging({
    params: { patientId },
    dependencies: [patientId],
    options: { enabled: withAging && !!patientId }
  });

  const calcInvoice = useMemo(() => {
    const balance = numOrDefault(invoice?.invoice_balance ?? invoice?.balance);

    return {
      ...invoice,
      balance,
      total_amount: balance,
      procedures: iaRa(invoice?.procedures),
      products: iaRa(invoice?.products),
      packages: iaRa(invoice?.packages)
    };
  }, [invoice]);

  const detailRows = useMemo(
    () => formatInvoiceProcedures({ invoices: getInvoiceDetails(calcInvoice, claims) }),
    [calcInvoice, claims]
  );

  const totals = useMemo(
    () => getProceduresTotal({ invoices: [calcInvoice], show: true, isAEOB }),
    [calcInvoice, isAEOB]
  );

  const keys = useMemo(
    () => visibleMoneyKeys({ rows: detailRows, hidden, claims }),
    [detailRows, hidden, claims]
  );
  const landscape = isLandscape(keys);

  const columns = useMemo(() => buildColumns({ keys, landscape }), [keys, landscape]);

  const sections = useMemo(
    () => buildSections({ rows: detailRows, keys, claims, timezone, columns, status, hidden }),
    [detailRows, keys, claims, timezone, columns, status, hidden]
  );

  const hasConfirmed = sections.some((section) => section.key === 'confirmed');
  const hasGfe = sections.some((section) => section.key === 'gfe');
  const combined = hasConfirmed && hasGfe;
  const isGfe = hasGfe && !hasConfirmed;

  const clientSurcharge = surchargeProp ?? (surchargeEnabled ? invoice?.surcharge_amount : null);
  let surcharge;
  if (clientSurcharge != null) surcharge = numOrDefault(clientSurcharge);
  else if (externalInvoice) surcharge = numOrDefault(externalInvoice.surcharge_amount);

  const techFee =
    surcharge != null
      ? surcharge
      : surchargeEnabled
        ? Math.round(
            numOrDefault(totals?.total) * (numOrDefault(practice?.surcharge_percentage) / 100)
          )
        : 0;

  const amountDue = numOrDefault(totals?.total) + techFee;

  const invoiceNumber = invoice?.id ? `#${invoice.id}` : '';
  const invoiceDate = hidden.has('date_of_service')
    ? ''
    : dateMedium(invoice?.dateofservice || invoice?.created_at, timezone);
  const dueDate = hidden.has('due_date')
    ? ''
    : dateMedium(externalInvoice?.due_date || invoice?.due_date, timezone);
  // The "Due date" slot still shows when the invoice has none; only hiding the field removes it.
  const billedToDueDate = hidden.has('due_date') ? '' : dueDate || '-';

  const party = buildParty({ patient: invoice?.patient, hidden, timezone });

  const balances = {
    confirmed: sumBalances(detailRows, (item) => !isGfeRow(item, status)),
    gfe: sumBalances(detailRows, (item) => isGfeRow(item, status)),
    combined,
    isGfe
  };

  const variant = combined ? 'combined' : isGfe ? 'gfe' : 'confirmed';

  const blocks = buildInvoiceBlocks({
    party,
    billedToAction,
    billedToDueDate,
    dueDate,
    sections,
    legend: buildLegend(keys),
    memo: memoText(externalInvoice?.memo) || memoText(invoice?.memo),
    disclaimer: hasGfe ? LEGAL : '',
    aging: buildAging(agingData?.aging),
    summaryRows: buildSummaryRows({ totals, balances, techFee, rows: detailRows }),
    summaryFootnote: buildSummaryFootnote({ totals, isGfe: hasGfe }),
    amountDue: money(amountDue)
  });

  const compactMeta = [
    invoiceDate ? `Issued ${invoiceDate}` : '',
    party.name,
    patientId && !hidden.has('pt_id') && !billedToAction ? `Chart #${patientId}` : ''
  ]
    .filter(Boolean)
    .join(' · ');

  return {
    practice,
    blocks,
    landscape,
    isGfe,
    invoiceNumber,
    compactMeta,
    docLines: buildDocLines({
      number: invoiceNumber,
      issuedDate: invoiceDate,
      dueDate,
      dueAmount: money(amountDue)
    }),
    legal: buildFooterLegal({
      practiceName: getPracticeName(practice),
      invoiceNumber,
      variantLabel: VARIANT_LABEL[variant],
      dueDate
    })
  };
};
